import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, Lock, ArrowLeft, Loader } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/Toast';
import { cognitoAuth } from '../services/cognito';
import { passkey } from '../services/passkey';
import { theme } from '../styles/theme';
import { Button, Label, FormGroup } from '../components/ui';
import { getPasswordError } from '../utils/password';
import logoColor from '../assets/libben-smart.png';

const GOOGLE_LOGIN_URL = cognitoAuth.getGoogleLoginUrl?.();

const Login = () => {
  const { t } = useTranslation(['auth', 'common']);
  const toast = useToast();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [askPasskey, setAskPasskey] = useState(false);
  const [forgot, setForgot]         = useState(false); // etapa 1: digitar email
  const [resetCode, setResetCode]   = useState(false); // etapa 2: digitar código + nova senha
  const [resetEmail, setResetEmail] = useState('');
  const [resetCodeVal, setResetCodeVal] = useState('');
  const [newPassword, setNewPassword]   = useState('');
  const navigate = useNavigate();

  const doNavigate = (user) => {
    const { role, tenantId } = user;
    if (role === 'sallus_admin')  navigate('/sallus/dashboard');
    else if (role === 'manager')   navigate(`/manager/${tenantId}`);
    else if (role === 'agent')     navigate(`/callcenter/${tenantId}`);
    else if (tenantId)             navigate(`/dashboard/${tenantId}`);
    else                           navigate('/tenants');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await cognitoAuth.signIn(email, password);
      if (result.success) {
        localStorage.setItem('user', JSON.stringify(result.user));
        // Sugere registrar biometria se suportado e ainda não registrado
        if (passkey.supported() && !passkey.isRegistered()) {
          setAskPasskey(true);
        } else {
          doNavigate(result.user);
        }
      } else {
        setError(result.error || t('invalidCredentials'));
      }
    } catch {
      setError(t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const creds = await passkey.authenticate();
      const result = await cognitoAuth.signIn(creds.email, creds.password);
      if (result.success) {
        localStorage.setItem('user', JSON.stringify(result.user));
        doNavigate(result.user);
      } else {
        setError(result.error || t('invalidCredentials'));
      }
    } catch (err) {
      setError(err.message || t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterPasskey = async () => {
    try {
      await passkey.register(email, password);
      toast.success(t('passkeyRegistered', { defaultValue: 'Biometria ativada! Use Face ID nos próximos acessos.' }));
    } catch (err) {
      toast.warning(err.message);
    }
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    doNavigate(user);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await cognitoAuth.forgotPassword(resetEmail);
      if (result.success) {
        toast.success(t('recoveryLinkSent') + ' ' + resetEmail);
        setForgot(false);
        setResetCode(true); // avança para etapa 2
      } else {
        setError(t('recoveryError'));
      }
    } catch {
      setError(t('recoveryError'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPassword = async (e) => {
    e.preventDefault();
    const pwErr = getPasswordError(newPassword);
    if (pwErr) { setError(pwErr); return; }
    setLoading(true);
    try {
      const result = await cognitoAuth.confirmPassword(resetEmail, resetCodeVal, newPassword);
      if (result.success) {
        toast.success(t('passwordChanged', { defaultValue: 'Senha alterada com sucesso! Faça login.' }));
        setResetCode(false);
        setResetEmail(''); setResetCodeVal(''); setNewPassword('');
      } else {
        setError(result.error);
      }
    } catch {
      setError(t('recoveryError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Glow />
      <Card>
        <LogoArea>
          <LogoColorImg src={logoColor} alt="Libben Agents" />
          <Tagline>{t('tagline')}</Tagline>
        </LogoArea>

        {askPasskey ? (
          <PasskeyPrompt>
            <PasskeyIcon>🔐</PasskeyIcon>
            <PasskeyTitle>{t('passkeyTitle', { defaultValue: 'Ativar login biométrico?' })}</PasskeyTitle>
            <PasskeyDesc>{t('passkeyDesc', { defaultValue: 'Use Face ID ou impressão digital para entrar mais rápido nos próximos acessos.' })}</PasskeyDesc>
            <Button style={{ width: '100%' }} onClick={handleRegisterPasskey} disabled={loading}>
              {t('passkeyEnable', { defaultValue: 'Ativar biometria' })}
            </Button>
            <BackLink type="button" onClick={() => { setAskPasskey(false); doNavigate(JSON.parse(localStorage.getItem('user') || '{}')); }}>
              {t('passkeySkip', { defaultValue: 'Agora não' })}
            </BackLink>
          </PasskeyPrompt>
        ) : resetCode ? (
          <form onSubmit={handleConfirmPassword}>
            <FormGroup>
              <Label>{t('resetCode', { defaultValue: 'Código recebido por e-mail' })}</Label>
              <InputWrapper>
                <InputIcon><Mail size={16} /></InputIcon>
                <StyledInput
                  type="text"
                  value={resetCodeVal}
                  onChange={e => setResetCodeVal(e.target.value)}
                  placeholder={t('resetCodePlaceholder', { defaultValue: 'Digite o código' })}
                  required
                />
              </InputWrapper>
            </FormGroup>
            <FormGroup>
              <Label>{t('newPassword', { defaultValue: 'Nova senha' })}</Label>
              <InputWrapper>
                <InputIcon><Lock size={16} /></InputIcon>
                <StyledInput
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder={t('newPasswordPlaceholder', { defaultValue: 'Mínimo 8 caracteres' })}
                  required
                />
              </InputWrapper>
            </FormGroup>
            {error && <ErrorBox>{error}</ErrorBox>}
            <Button type="submit" style={{ width: '100%', marginTop: theme.spacing[4] }} disabled={loading}>
              {loading ? <Loader size={16} className="spin" /> : t('confirmNewPassword', { defaultValue: 'Confirmar nova senha' })}
            </Button>
            <BackLink type="button" onClick={() => { setResetCode(false); setForgot(true); setError(''); }}>
              <ArrowLeft size={14} /> {t('back', { ns: 'common' })}
            </BackLink>
          </form>
        ) : forgot ? (
          <form onSubmit={handleForgot}>
            <FormGroup>
              <Label>{t('recoveryEmail')}</Label>
              <InputWrapper>
                <InputIcon><Mail size={16} /></InputIcon>
                <StyledInput
                  type="email"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  required
                />
              </InputWrapper>
            </FormGroup>
            {error && <ErrorBox>{error}</ErrorBox>}
            <Button type="submit" style={{ width: '100%', marginTop: theme.spacing[4] }} disabled={loading}>
              {loading ? <Loader size={16} className="spin" /> : t('sendRecoveryLink')}
            </Button>
            <BackLink type="button" onClick={() => { setForgot(false); setError(''); }}>
              <ArrowLeft size={14} /> {t('backToLogin')}
            </BackLink>
          </form>
        ) : (
          <form onSubmit={handleLogin}>
            <Fields>
              <FormGroup>
                <Label>{t('email')}</Label>
                <InputWrapper>
                  <InputIcon><Mail size={16} /></InputIcon>
                  <StyledInput
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={t('emailPlaceholder')}
                    required
                    autoComplete="email"
                  />
                </InputWrapper>
              </FormGroup>

              <FormGroup>
                <Label>{t('password')}</Label>
                <InputWrapper>
                  <InputIcon><Lock size={16} /></InputIcon>
                  <StyledInput
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                </InputWrapper>
              </FormGroup>
            </Fields>

            {error && <ErrorBox>{error}</ErrorBox>}

            <Button type="submit" style={{ width: '100%', marginTop: theme.spacing[5] }} disabled={loading} size="lg">
              {loading ? <><Loader size={16} className="spin" /> {t('signingIn')}</> : t('signIn')}
            </Button>

            {GOOGLE_LOGIN_URL && (
              <GoogleBtn href={GOOGLE_LOGIN_URL}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                {t('signInWithGoogle', { defaultValue: 'Entrar com Google' })}
              </GoogleBtn>
            )}

            <BackLink type="button" onClick={() => { setForgot(true); setError(''); }}>
              {t('forgotPassword')}
            </BackLink>

            {passkey.supported() && passkey.isRegistered() && (
              <PasskeyBtn type="button" onClick={handlePasskeyLogin} disabled={loading}>
                🔒 {t('passkeyLogin', { defaultValue: 'Entrar com biometria' })}
              </PasskeyBtn>
            )}

            <RegisterLink>
              {t('noAccount')} <Link to="/register">{t('createAccount')}</Link>
            </RegisterLink>
          </form>
        )}

        {process.env.REACT_APP_ENVIRONMENT === 'staging' && (
          <EnvBadge>Staging</EnvBadge>
        )}
      </Card>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  min-height: 100vh;
  background: ${theme.colors.gray900};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
  position: relative;
  overflow: hidden;
`;

const Glow = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, ${theme.colors.primary}33 0%, transparent 70%);
  top: -200px;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
`;

const Card = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.radii['2xl']};
  padding: ${theme.spacing[10]};
  width: 100%;
  max-width: 480px;
  box-shadow: ${theme.shadows.xl};
  position: relative;
  @media (max-width: 639px) {
    padding: ${theme.spacing[6]};
    border-radius: ${theme.radii.xl};
  }
`;

const LogoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${theme.spacing[8]};
`;

const LogoColorImg = styled.img`
  height: 48px;
  width: auto;
  object-fit: contain;
  margin-bottom: ${theme.spacing[3]};
`;

const Tagline = styled.p`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
  margin: 0;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const InputWrapper = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  pointer-events: none;
  display: flex;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[10]};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  transition: ${theme.transitions.fast};
  outline: none;
  &::placeholder { color: ${theme.colors.textMuted}; }
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

const ErrorBox = styled.div`
  margin-top: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.dangerBorder};
  border-radius: ${theme.radii.md};
  color: #991b1b;
  font-size: ${theme.typography.sm};
  text-align: center;
`;

const BackLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[1]};
  width: 100%;
  margin-top: ${theme.spacing[3]};
  background: none;
  border: none;
  color: ${theme.colors.primary};
  font-size: ${theme.typography.sm};
  cursor: pointer;
  padding: ${theme.spacing[2]};
  border-radius: ${theme.radii.md};
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.primaryLight}; }
`;

const EnvBadge = styled.div`
  position: absolute;
  top: ${theme.spacing[3]};
  right: ${theme.spacing[3]};
  padding: 2px ${theme.spacing[2]};
  background: ${theme.colors.warningLight};
  color: #92400e;
  border: 1px solid ${theme.colors.warningBorder};
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
`;

const RegisterLink = styled.div`
  text-align: center;
  margin-top: ${theme.spacing[3]};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
  a {
    color: ${theme.colors.primary};
    font-weight: ${theme.typography.medium};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const PasskeyPrompt = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: ${theme.spacing[4]}; text-align: center; padding: ${theme.spacing[2]} 0;
`;
const PasskeyIcon = styled.div`font-size: 3rem; line-height: 1;`;
const PasskeyTitle = styled.div`
  font-size: ${theme.typography.lg}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const PasskeyDesc = styled.div`
  font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; line-height: 1.5;
`;
const PasskeyBtn = styled.button`
  width: 100%; margin-top: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.gray50}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary}; cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.primaryLight}; border-color: ${theme.colors.primary}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const GoogleBtn = styled.a`
  display: flex; align-items: center; justify-content: center; gap: ${theme.spacing[2]};
  width: 100%; margin-top: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: white; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary}; text-decoration: none; cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.gray50}; border-color: #4285F4; }
`;

export default Login;
